import React from 'react';

const CO2eShowComponent = ({
  co2ePerKg,
  size,
  unit_id,
  darkMode = false,
  cookedCo2ePerKg,
  cookingWeightLoss,
  /** Portion size in the same unit as `size` (e.g. grams). Used to show CO2e per portion. */
  portionSize,
  /** When true, always span full container width (e.g. public dish column uses viewport breakpoints, not container width). */
  fullWidth = false,
}) => {
  // Conversion function
  const convertToKgOrLiter = (size, unit_id) => {
    if (!size || !unit_id) return 0; // Handle missing values

    switch (unit_id) {
      case 2: // KGM (Kilograms)
        return size; // Already in kg
      case 3: // GRM (Grams)
        return size / 1000; // Convert grams to kg
      case 4: // MGM (Milligrams)
        return size / 1000000; // Convert mg to kg
      case 6: // LTR (Liters)
        return size; // Already in liters
      case 5: // MLT (Milliliters)
        return size / 1000; // Convert ml to liters
      case 11: // DLT (Deciliters)
        return size / 10; // Convert dl to liters
      case 1: // H87 (styck or piece)
        return size; // Assume each piece is equivalent to the size directly
      default:
        return 0; // Unsupported unit_id
    }
  };

  try {
    // Convert co2ePerKg to number and handle null/undefined/NaN
    const co2ePerKgNumber = co2ePerKg && !isNaN(parseFloat(co2ePerKg)) ? parseFloat(co2ePerKg) : 0;
    
    // Convert size to kg or liters
    const convertedSize = convertToKgOrLiter(size, unit_id);

    // Calculate total CO2e
    const co2eTotalNumber =
      convertedSize && co2ePerKgNumber ? convertedSize * co2ePerKgNumber : null;
    const co2eTotal =
      co2eTotalNumber !== null ? co2eTotalNumber.toFixed(2) : "N/A";

    const portionSizeNumber =
      portionSize && !isNaN(parseFloat(portionSize)) ? parseFloat(portionSize) : 0;
    const sizeNumber = size && !isNaN(parseFloat(size)) ? parseFloat(size) : 0;
    const co2ePerPortion =
      co2eTotalNumber !== null &&
      portionSizeNumber > 0 &&
      sizeNumber > 0
        ? (co2eTotalNumber / (sizeNumber / portionSizeNumber)).toFixed(2)
        : null;

    const widthClass = fullWidth ? 'w-full' : 'w-[70%] mobile:w-full';

    return (
      <div
        className={`flex justify-center items-center bg-transparent p-2 mb-2 ${widthClass} mx-auto rounded-full mobile:mt-8`}
        style={{ border: '1px solid #E5E7EB' }}
      >

        {/* CO2e / KG Section */}
        <div className="flex flex-col items-center justify-center px-0">
          <p className={`text-sm mobile:capitalize mobile:tracking-tight tracking-wide mb-1 ${
            darkMode ? 'text-gray-300' : 'text-gray-500'
          }`}>
            CO2e kg/kg: {co2ePerKgNumber ? co2ePerKgNumber.toFixed(2) : "N/A"}
          </p>
          {cookedCo2ePerKg > 0 && cookingWeightLoss > 0 && (
            <p className={`text-xs mobile:text-[10px] ${
              darkMode ? 'text-gray-400' : 'text-gray-400'
            }`}>
              Tillagad: {cookedCo2ePerKg.toFixed(2)} kg/kg
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="bg-gray-300 w-[1px] h-14 mx-6 rounded-full"></div>

        {/* Total CO2e Section */}
        <div className="flex flex-col items-center justify-center px-0">
          <p className={`text-sm mobile:capitalize mobile:tracking-tight tracking-wide mb-1 ${
            darkMode ? 'text-gray-300' : 'text-gray-500'
          }`}>
            Total CO2e kg: {co2eTotal}
          </p>
          {co2ePerPortion !== null && (
            <p className={`text-xs mobile:text-[10px] ${
              darkMode ? 'text-gray-400' : 'text-gray-400'
            }`}>
              CO2e per portion kg: {co2ePerPortion}
            </p>
          )}
        </div>

      </div>
    );
  } catch (error) {
    console.error('CO2eShowComponent error:', error);
    console.log('CO2eShowComponent props:', { co2ePerKg, size, unit_id });
    return (
      <div className="flex justify-center items-center bg-white border border-gray-200 rounded-full p-5 shadow-sm my-4 w-full mx-auto">
        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Error displaying CO2e data</p>
      </div>
    );
  }
};

export default CO2eShowComponent;
